import React from "react";

const POLL_INTERVAL_MS = 1500;
const MAX_NO_GROWTH_POLLS = 2;
const MAX_CONSECUTIVE_FETCH_FAILURES = 3;

export type PollUntilStableOptions<T extends { content: string }> = {
  fetchMessages: () => Promise<T[]>;
  conversationId: string;
  selectedConversationIdRef: React.MutableRefObject<string | null | undefined>;
  setMessages: React.Dispatch<React.SetStateAction<T[]>>;
  onGrowth?: () => void;
  keepPolling?: (messages: T[]) => boolean;
  isPollCurrent?: () => boolean;
};

export async function pollUntilStable<T extends { content: string }>(
  options: PollUntilStableOptions<T>,
): Promise<void> {
  const {
    fetchMessages,
    conversationId,
    selectedConversationIdRef,
    setMessages,
    onGrowth,
    keepPolling,
    isPollCurrent,
  } = options;
  const isSuperseded = (): boolean => isPollCurrent !== undefined && !isPollCurrent();
  let prevSig: string | null = null;
  let noGrowthCount = 0;
  let fetchFailureCount = 0;
  let lastMessages: T[] | null = null;

  while (selectedConversationIdRef.current === conversationId) {
    await new Promise<void>(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
    if (selectedConversationIdRef.current !== conversationId || isSuperseded()) break;

    let messages: T[];
    try {
      messages = await fetchMessages();
    } catch (error) {
      fetchFailureCount++;
      const keepPollingThroughFailures =
        keepPolling !== undefined && (lastMessages === null || keepPolling(lastMessages));
      if (!keepPollingThroughFailures && fetchFailureCount >= MAX_CONSECUTIVE_FETCH_FAILURES) {
        throw error;
      }
      continue;
    }
    fetchFailureCount = 0;
    lastMessages = messages;

    if (selectedConversationIdRef.current !== conversationId || isSuperseded()) break;

    const lastMsg = messages[messages.length - 1];
    const sig = `${messages.length}:${lastMsg?.content ?? ""}`;

    if (sig !== prevSig) {
      const isBaselinePoll = prevSig === null;
      prevSig = sig;
      noGrowthCount = 0;
      setMessages(messages);
      if (!isBaselinePoll) onGrowth?.();
    } else {
      noGrowthCount++;
      if (noGrowthCount >= MAX_NO_GROWTH_POLLS) {
        if (!keepPolling) break;
        if (!keepPolling(messages)) {
          setMessages(messages);
          break;
        }
        noGrowthCount = 0;
      }
    }
  }
}
